import AddIcon from '@material-ui/icons/Add';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import CustomAutoComplete from 'src/components/autoComplete/autoComplete';
import { fetchAllOrders } from 'src/store/slices/omsSlice';
import { AppDispatch } from 'src/store/store';
import axiosInstance from 'src/utils/axios';
import * as Yup from 'yup';

const AddOrderDialog = ({ open, onClose }: any) => {
  const validationSchema = Yup.object().shape({
    organization: Yup.object().nullable().required('Customer is required'),
    products: Yup.array()
      .of(
        Yup.object().shape({
          product: Yup.object().required('Product is required'),
          unitPrice: Yup.number().required('Unit Price is required'),
          quantity: Yup.number().required('Quantity is required'),
          discount: Yup.number(),
        })
      )
      .min(1, 'At least one product must be added') // Ensure the array has at least one product
      .required('Products are required'),
  });
  const dispatch = useDispatch<AppDispatch>();

  const initialValues = {
    organization: null,
    description: '',
    newProduct: { product: null, unitPrice: '', quantity: 1, discount: 0 },
    products: [],
    catalogs: [],
    categories: [],
  };

  const [organizations, setOrganizations] = useState([]);
  const [newProductErrors, setNewProductErrors] = useState({
    product: '',
    unitPrice: '',
    quantity: '',
  });

  const handleSubmit = async (values) => {
    const payload = {
      products: values.products.map((product) => ({
        productId: product.product._id,
        productName: product.product.productTemplateObject.Name,
        sku: product.product.productTemplateObject.SKU,
        actualPrice: product.unitPrice,
        discount: product.discount,
        // buyInPrice: product.unitPrice,
        quantity: product.quantity,
      })),
      organization: {
        orgId: values.organization._id,
        name: values.organization.name,
        email: values.organization.email,
      },
      description: values.description,
      customFields: [],
    };

    axiosInstance
      .post(`${process.env.REACT_APP_OMS_URL}/order`, payload)
      .then((response) => {
        toast.success(response?.data?.message ?? 'Order Created Successfully');
        dispatch(fetchAllOrders(``));
        onClose();
      })
      .catch((error) => {
        toast.error(error.response?.data?.message ?? 'Error creating order');
      });
  };

  const calculateSubtotal = (products) =>
    products?.reduce(
      (total, { unitPrice, quantity }) =>
        total + (Number(unitPrice) || 0) * (Number(quantity) || 0),
      0
    );

  const calculateDiscount = (products) =>
    products.reduce(
      (total, { unitPrice, quantity, discount }) =>
        total +
        ((Number(unitPrice) || 0) *
          (Number(quantity) || 0) *
          (Number(discount) || 0)) /
          100,
      0
    );

  const getAllOrganizations = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_OMS_URL}/autoComplete/organizations`
      );
      if (response.status === 200) {
        setOrganizations(response.data);
      }
    } catch (error) {
      console.error('Error fetching organizations', error);
    }
  };
  //   const totalDue = subtotal - discount + tax;

  useEffect(() => {
    getAllOrganizations();
  }, []);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)', // Adjust the alpha value for transparency
        },
      }}>
      <DialogTitle
        sx={{
          fontSize: '20px',
        }}>
        Add New Order
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}>
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            validateForm,
          }) => (
            <Form>
              {/* Customer Information Fields */}
              {console.log('catalogs', values?.catalogs)}
              {console.log('categories', values?.categories)}

              <div
                style={{
                  marginTop: '16px',
                  marginBottom: '8px',
                }}>
                <CustomAutoComplete
                  id="order-catalogs"
                  value={values?.catalogs}
                  onSelect={(e: any, newValue: any) => {
                    // If all catalogs are removed
                    if (!newValue?.length) {
                      setFieldValue('catalogs', []);
                      setFieldValue('categories', []);
                      setFieldValue('newProduct', {
                        product: null,
                        unitPrice: '',
                        quantity: 1,
                        discount: 0,
                      });
                      return;
                    }

                    const removedCatalogs = values.catalogs.filter(
                      (oldCatalog) =>
                        !newValue.some(
                          (newCat) => newCat._id === oldCatalog._id
                        )
                    );

                    // If any catalog was removed, clear all categories
                    if (removedCatalogs.length > 0) {
                      setFieldValue('categories', []);
                      setFieldValue('newProduct', {
                        product: null,
                        unitPrice: '',
                        quantity: 1,
                        discount: 0,
                      });
                    }

                    setFieldValue('catalogs', newValue);
                  }}
                  multiple
                  placeholder={values?.catalogs.length === 0 ? 'Catalog' : ''}
                  label="Catalog"
                  apiUrl={`${process.env.REACT_APP_OMS_URL}/autoComplete/catalogues`}
                />
              </div>

              <div
                style={{
                  marginTop: '16px',
                  marginBottom: '8px',
                }}>
                <CustomAutoComplete
                  id="order-category"
                  value={values?.categories}
                  onSelect={(e: any, newValue: any) => {
                    setFieldValue('categories', newValue);
                    setFieldValue('newProduct', {
                      product: null,
                      unitPrice: '',
                      quantity: 1,
                      discount: 0,
                    });
                    // setFieldValue('products', []);
                  }}
                  multiple
                  placeholder="Category"
                  label="Category"
                  disabled={!values?.catalogs?.length}
                  // error={Boolean(
                  //   formik?.touched?.categories && formik?.errors?.categories
                  // )}
                  // helperText={
                  //   formik?.touched?.categories && formik?.errors?.categories
                  // }
                  // args={{
                  //   catalogueId: formik?.values?.catalogs?.map(
                  //     (catalog) => catalog._id
                  //   ),
                  // }}
                  args={{
                    catalogueId: values?.catalogs
                      .map((catalog) => catalog?._id)
                      .join(','),
                  }}
                  apiUrl={`${process.env.REACT_APP_OMS_URL}/autoComplete/category`}
                />
              </div>

              <Autocomplete
                options={organizations}
                // onChange={handleChange}
                getOptionLabel={(option) => option.name}
                onChange={(e: any, newValue: any) => {
                  setFieldValue('organization', newValue);
                }}
                value={values.organization}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Customer Name"
                    // name="organization"
                    // value={values.organization}
                    // onBlur={handleBlur}
                    variant="outlined"
                    margin="normal"
                    error={touched.organization && Boolean(errors.organization)}
                    helperText={touched.organization && errors.organization}
                  />
                )}
              />
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                margin="normal"
                error={touched.description && Boolean(errors.description)}
                helperText={touched.description && errors.description}
              />

              {/* Table to Add and List Products */}
              <Table>
                <TableHead sx={{ backgroundColor: '#EDF2F7' }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        minWidth: '200px',
                      }}>
                      Product Name - <b>SKU</b>
                    </TableCell>
                    <TableCell>Unit Price</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Discount %</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell
                      sx={{
                        textWrap: 'noWrap',
                      }}>
                      Discount Price
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Displaying Added Products */}
                  {values.products.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableRow key={index}>
                      <TableCell>
                        {item.product.productTemplateObject.Name} -
                        <b>{item.product.productTemplateObject.SKU}</b>
                      </TableCell>
                      <TableCell>{item.unitPrice}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.discount}</TableCell>
                      <TableCell>
                        <Typography>
                          {(
                            (item.unitPrice || 0) * (item.quantity || 0)
                          ).toFixed(2)}
                          €
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {(
                            (item.unitPrice || 0) *
                            (item.quantity || 0) *
                            (1 - (item.discount || 0) / 100)
                          ).toFixed(2)}
                          €
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() => {
                            const updatedProducts = values.products.filter(
                              (_, idx) => idx !== index
                            );
                            setFieldValue('products', updatedProducts);
                          }}>
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* New Product Row */}
                  <TableRow>
                    <TableCell>
                      <CustomAutoComplete
                        id="order-Product"
                        value={values.newProduct.product}
                        onSelect={(e: any, newValue: any) => {
                          setNewProductErrors({
                            ...newProductErrors,
                            product: '',
                          });
                          setFieldValue('newProduct', {
                            ...values.newProduct,
                            product: newValue,
                          });
                        }}
                        placeholder="Product Name"
                        label="Product Name"
                        isProducts
                        multiple={false}
                        disabled={!values?.categories?.length}
                        error={Boolean(newProductErrors.product)}
                        helperText={newProductErrors.product}
                        args={{
                          categoryId: values?.categories
                            .map((category) => category?._id)
                            .join(','),
                        }}
                        apiUrl={`${process.env.REACT_APP_OMS_URL}/autoComplete/product`}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        placeholder="Unit Price"
                        type="number"
                        name="newProduct.unitPrice"
                        value={values.newProduct.unitPrice}
                        inputProps={{ min: 0 }}
                        onChange={(e) => {
                          setNewProductErrors({
                            ...newProductErrors,
                            unitPrice: '',
                          });
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                        error={Boolean(newProductErrors.unitPrice)}
                        helperText={newProductErrors.unitPrice}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        placeholder="Quantity"
                        type="number"
                        name="newProduct.quantity"
                        inputProps={{ min: 0 }}
                        value={values.newProduct.quantity}
                        // onChange={handleChange}
                        onChange={(e) => {
                          setNewProductErrors({
                            ...newProductErrors,
                            quantity: '',
                          });
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                        error={Boolean(newProductErrors.quantity)}
                        helperText={newProductErrors.quantity}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        placeholder="Discount"
                        type="number"
                        name="newProduct.discount"
                        inputProps={{ min: 0 }}
                        value={values.newProduct.discount}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {(
                          (values.newProduct.unitPrice || 0) *
                          (values.newProduct.quantity || 0)
                        ).toFixed(2)}
                        €
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {(
                          (values.newProduct.unitPrice || 0) *
                          (values.newProduct.quantity || 0) *
                          (1 - (values.newProduct.discount || 0) / 100)
                        ).toFixed(2)}
                        €
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {errors.products && touched.products && (
                <p
                  style={{
                    color: 'red',
                    fontSize: '12px',
                    marginTop: '8px',
                  }}>
                  {errors.products}
                </p>
              )}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '15px',
                }}>
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ marginTop: 1, maxHeight: '40px' }}
                  onClick={() => {
                    const { product, unitPrice, quantity } = values.newProduct;

                    // Check if product already exists in the products array
                    const isProductExists = values.products.some(
                      (existingProduct) =>
                        existingProduct.product._id === product?._id
                    );

                    if (isProductExists) {
                      toast.error('This product has already been added');
                      return;
                    }

                    setNewProductErrors({
                      product: product ? '' : 'Product is required',
                      unitPrice: unitPrice > 0 ? '' : 'Unit Price is required',
                      quantity: quantity > 0 ? '' : 'Quantity is required',
                    });

                    if (
                      !errors.newProduct &&
                      values.newProduct.product &&
                      values.newProduct.unitPrice &&
                      values.newProduct.quantity > 0
                    ) {
                      setFieldValue('products', [
                        ...values.products,
                        values.newProduct,
                      ]);
                      setFieldValue('newProduct', {
                        product: null,
                        unitPrice: '',
                        quantity: 1,
                        discount: 0,
                      });
                    }
                  }}
                  startIcon={<AddIcon />}>
                  Add
                </Button>
                <div>
                  <Typography>
                    Subtotal: {calculateSubtotal(values.products).toFixed(2)}€
                  </Typography>
                  <Typography>
                    Discount: {calculateDiscount(values.products).toFixed(2)}€
                  </Typography>
                  <Typography>
                    Total Due:{' '}
                    {calculateSubtotal(values.products).toFixed(2) -
                      calculateDiscount(values.products).toFixed(2)}
                    €
                  </Typography>
                </div>
              </Box>

              {/* Action Buttons */}
              <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={onClose} variant="outlined">
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="secondary">
                  Create Order
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AddOrderDialog;
